import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface AuditAccount {
  id: string
  name: string
}

interface AuditMccGroup {
  mccId: string
  mccName: string
  accounts: AuditAccount[]
}

const GRADIENT = 'linear-gradient(135deg,#ff6d11,#fc1cc7)'

export default function AuditSelectAccount() {
  const [searchParams] = useSearchParams()
  const selection = searchParams.get('selection')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [groups, setGroups] = useState<AuditMccGroup[]>([])

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!selection) {
      setLoading(false)
      setError('Lien invalide — recommence depuis le début')
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetch(`/api/public-audit/select/${selection}`)
      .then(async (res) => {
        const body = await res.json()
        if (cancelled) return
        if (res.ok && body.success) {
          setEmail(body.data.email)
          setGroups(body.data.groups)
        } else {
          setError(body.error || "Une erreur est survenue")
        }
      })
      .catch(() => {
        if (!cancelled) setError("Une erreur est survenue")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selection])

  if (loading) {
    return (
      <div style={{ maxWidth: 560, margin: '80px auto', textAlign: 'center', padding: 24 }}>
        <p>Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ maxWidth: 560, margin: '80px auto', textAlign: 'center', padding: 24 }}>
        <p style={{ color: '#fc1cc7' }}>{error}</p>
      </div>
    )
  }

  const normalizedSearch = search.trim().toLowerCase()
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      accounts: group.accounts.filter((account) => {
        if (!normalizedSearch) return true
        return (
          account.name.toLowerCase().includes(normalizedSearch) ||
          account.id.toLowerCase().includes(normalizedSearch)
        )
      }),
    }))
    .filter((group) => group.accounts.length > 0)

  const selectedAccount = groups
    .flatMap((g) => g.accounts)
    .find((a) => a.id === selectedId)

  const selectAccount = (id: string) => {
    setSelectedId(id)
    setOpen(false)
  }

  const changeSelection = () => {
    setSelectedId(null)
    setSearch('')
    setOpen(true)
  }

  const handleSubmit = () => {
    if (!selectedId || !selection) return
    setSubmitting(true)
    setSubmitError(null)

    fetch(`/api/public-audit/select/${selection}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId: selectedId }),
    })
      .then(async (res) => {
        const body = await res.json()
        if (res.ok && body.success) {
          window.location.href = `/rapport/${body.data.slug}/${body.data.publicToken}`
        } else {
          setSubmitError(body.error || 'Une erreur est survenue')
          setSubmitting(false)
        }
      })
      .catch(() => {
        setSubmitError('Une erreur est survenue')
        setSubmitting(false)
      })
  }

  return (
    <div style={{ maxWidth: 520, margin: '80px auto', padding: 24 }}>
      <h1 style={{ textAlign: 'center', fontSize: 24 }}>Choisissez le compte à auditer</h1>
      {email && (
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
          Connecté en tant que {email}
        </p>
      )}

      <div style={{ marginTop: 28 }}>
        {selectedAccount ? (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '14px 18px', borderRadius: 14, border: '1px solid #e2e8f0',
              background: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
            }}
          >
            <span style={{ fontWeight: 600, color: '#212140' }}>{selectedAccount.name}</span>
            <button
              type="button"
              onClick={changeSelection}
              style={{
                background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600,
                color: '#ff6d11', cursor: 'pointer',
              }}
            >
              Changer
            </button>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                border: '1px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box',
                outline: 'none',
              }}
            />

            {open && (
              <div
                style={{
                  marginTop: 8, borderRadius: 14, border: '1px solid #e2e8f0', background: '#fff',
                  boxShadow: '0 8px 24px rgba(15,23,42,0.08)', overflow: 'hidden',
                  maxHeight: 320, overflowY: 'auto',
                }}
              >
                {filteredGroups.length === 0 && (
                  <p style={{ padding: '16px 18px', fontSize: 13, color: '#94a3b8', margin: 0 }}>
                    Aucun compte ne correspond à cette recherche.
                  </p>
                )}
                {filteredGroups.map((group) => (
                  <div key={group.mccId}>
                    <div
                      style={{
                        padding: '10px 18px 4px', fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.4, color: '#94a3b8',
                      }}
                    >
                      {group.mccName}
                    </div>
                    {group.accounts.map((account) => (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() => selectAccount(account.id)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', border: 'none',
                          background: 'transparent', padding: '10px 18px', fontSize: 14,
                          color: '#212140', cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f2ee')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {account.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={!selectedId || submitting}
        onClick={handleSubmit}
        style={{
          width: '100%', marginTop: 24, padding: '14px 20px', borderRadius: 12,
          border: 'none', fontWeight: 600, cursor: !selectedId || submitting ? 'not-allowed' : 'pointer',
          background: !selectedId || submitting ? '#94a3b8' : GRADIENT,
          color: '#fff',
        }}
      >
        {submitting ? 'Envoi...' : 'Lancer l\'audit sur ce compte'}
      </button>

      {submitError && (
        <p style={{ color: '#fc1cc7', marginTop: 16, textAlign: 'center' }}>{submitError}</p>
      )}
    </div>
  )
}
